import api from './api';
import { Contrato, CreateContratoDto, PagoProgramado, PaginatedResponse } from '../types';

class ContractsService {
  async getContratos(page = 1, limit = 10, search?: string, expiring?: boolean): Promise<PaginatedResponse<Contrato>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }

    if (expiring) {
      params.append('expiring', 'true');
    }

    const response = await api.get<PaginatedResponse<Contrato>>(`/contratos?${params}`);
    return response.data;
  }

  async getContratoById(id: number): Promise<Contrato> {
    const response = await api.get<Contrato>(`/contratos/${id}`);
    return response.data;
  }

  async createContrato(contrato: CreateContratoDto): Promise<Contrato> {
    const response = await api.post<Contrato>('/contratos', contrato);
    return response.data;
  }

  async updateContrato(id: number, contrato: Partial<Contrato>): Promise<Contrato> {
    const response = await api.put<Contrato>(`/contratos/${id}`, contrato);
    return response.data;
  }

  async deleteContrato(id: number): Promise<void> {
    await api.delete(`/contratos/${id}`);
  }

  // Pagos Programados
  async getPagosProgramados(page = 1, limit = 10, search?: string, status?: string, isAlert = false): Promise<PaginatedResponse<PagoProgramado>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }

    if (status) {
      params.append('status', status);
    }

    if (isAlert) {
      params.append('alert', 'true');
    }

    const response = await api.get<PaginatedResponse<PagoProgramado>>(`/pagos-programados?${params}`);
    return response.data;
  }

  async createPagoProgramado(pago: {
    contratoId: number;
    fecha: string;
    monto: number;
    descripcion?: string;
  }): Promise<PagoProgramado> {
    const response = await api.post<PagoProgramado>('/pagos-programados', pago);
    return response.data;
  }

  async createMultiplePagosProgramados(data: {
    contratoId: number;
    numeroPagos: number;
    fechaInicio: string;
    montoPago: number;
  }): Promise<PagoProgramado[]> {
    const response = await api.post<PagoProgramado[]>('/pagos-programados/multiple', data);
    return response.data;
  }

  async updatePagoProgramado(id: number, pago: Partial<PagoProgramado>): Promise<PagoProgramado> {
    const response = await api.put<PagoProgramado>(`/pagos-programados/${id}`, pago);
    return response.data;
  }

  async getPagoProgramadoById(id: number): Promise<PagoProgramado> {
    const response = await api.get<PagoProgramado>(`/pagos-programados/${id}`);
    return response.data;
  }

  async deletePagoProgramado(id: number): Promise<void> {
    await api.delete(`/pagos-programados/${id}`);
  }

  // Detalles de Contrato
  async createDetalleContrato(contratoId: number, detalle: {
    conceptoId?: number;
    descripcion: string;
    cantidad: number;
    precioUnitario: number;
    descuentoPct?: number;
  }): Promise<any> {
    const response = await api.post(`/contratos/${contratoId}/detalles`, detalle);
    return response.data;
  }

  async updateDetalleContrato(detalleId: number, detalle: {
    descripcion?: string;
    cantidad?: number;
    precioUnitario?: number;
    descuentoPct?: number;
  }): Promise<any> {
    const response = await api.put(`/contratos/detalles/${detalleId}`, detalle);
    return response.data;
  }

  async deleteDetalleContrato(detalleId: number): Promise<void> {
    await api.delete(`/contratos/detalles/${detalleId}`);
  }

  // Convertir propuesta a contrato
  async convertirPropuestaAContrato(propuestaId: number, data: {
    fechaInicio: string;
    fechaFin: string;
    numeroContrato: string;
  }): Promise<Contrato> {
    const response = await api.post<Contrato>(`/contratos/convertir-propuesta/${propuestaId}`, data);
    return response.data;
  }

  // Archivos
  async uploadArchivo(contratoId: number, formData: FormData): Promise<any> {
    const response = await api.post(`/contratos/${contratoId}/archivos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteArchivo(contratoId: number, archivoId: number): Promise<void> {
    await api.delete(`/contratos/${contratoId}/archivos/${archivoId}`);
  }
}

const contractsService = new ContractsService();
export default contractsService;